import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../contexts/LanguageContext';
import { Lock, Eye, EyeOff, Check, AlertCircle } from 'lucide-react';
import { EMZI_COLORS, EMZI_RADIUS, EMZI_SHADOWS } from '../styles/emziCI';
import { secureStorage } from '../utils/secureStorage';
import { BACKEND_URL } from '../utils/backendUrl';

/**
 * EMZI NEXUS V6.3 - Change Password Page
 * For users with must_change_password=true flag
 * Mobile-first design (390×844)
 */

const ChangePasswordPage = () => {
  const t = useLang();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const validatePasswords = () => {
    if (formData.newPassword.length < 8) {
      setError(t('auth.changePassword.passwordTooShort'));
      return false;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setError(t('auth.changePassword.passwordsNoMatch'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validatePasswords()) {
      return;
    }

    try {
      setLoading(true);
            const token = secureStorage.getItem('nexus_access_token');

      const response = await fetch(`${BACKEND_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: formData.currentPassword,
          new_password: formData.newPassword,
          confirm_password: formData.confirmPassword
        })
      });

      if (response.ok) {
        setSuccess(true);
        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || t('auth.changePassword.errorMessage'));
      }
    } catch (err) {
      console.error('Password change error:', err);
      setError(t('auth.changePassword.errorMessage'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #E3F2FD 0%, #FFFFFF 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          background: 'white',
          borderRadius: EMZI_RADIUS.lg,
          boxShadow: EMZI_SHADOWS.xl,
          padding: '40px 24px',
          width: '100%',
          maxWidth: '400px',
          textAlign: 'center'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            background: '#10b981',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <Check size={32} color="white" />
          </div>
          <h2 style={{
            fontSize: '22px',
            fontWeight: '700',
            color: EMZI_COLORS.textPrimary,
            marginBottom: '12px'
          }}>
            {t('auth.changePassword.successMessage')}
          </h2>
          <p style={{
            fontSize: '14px',
            color: EMZI_COLORS.textSecondary,
            marginBottom: '24px'
          }}>
            Redirecting to dashboard...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #E3F2FD 0%, #FFFFFF 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: EMZI_RADIUS.lg,
        boxShadow: EMZI_SHADOWS.xl,
        padding: '32px 24px',
        width: '100%',
        maxWidth: '400px'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '56px',
            height: '56px',
            background: EMZI_COLORS.primary,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px'
          }}>
            <Lock size={28} color="white" />
          </div>
          <h1 style={{
            fontSize: '22px',
            fontWeight: '700',
            color: EMZI_COLORS.textPrimary,
            marginBottom: '8px'
          }}>
            {t('auth.changePassword.title')}
          </h1>
          <p style={{
            fontSize: '14px',
            color: EMZI_COLORS.textSecondary
          }}>
            {t('auth.changePassword.subtitle')}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ marginBottom: '20px' }}>
          {/* Current Password */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: EMZI_COLORS.textPrimary,
              marginBottom: '8px'
            }}>
              {t('auth.changePassword.currentPassword')}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPasswords ? 'text' : 'password'}
                value={formData.currentPassword}
                onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  paddingRight: '40px',
                  border: `1px solid ${EMZI_COLORS.border}`,
                  borderRadius: EMZI_RADIUS.sm,
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          {/* New Password */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: EMZI_COLORS.textPrimary,
              marginBottom: '8px'
            }}>
              {t('auth.changePassword.newPassword')}
            </label>
            <input
              type={showPasswords ? 'text' : 'password'}
              value={formData.newPassword}
              onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
              required
              minLength={8}
              style={{
                width: '100%',
                padding: '12px',
                border: `1px solid ${EMZI_COLORS.border}`,
                borderRadius: EMZI_RADIUS.sm,
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
            <div style={{
              fontSize: '12px',
              color: EMZI_COLORS.textSecondary,
              marginTop: '4px'
            }}>
              {t('auth.changePassword.passwordHint')}
            </div>
          </div>

          {/* Confirm Password */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: EMZI_COLORS.textPrimary,
              marginBottom: '8px'
            }}>
              {t('auth.changePassword.confirmPassword')}
            </label>
            <input
              type={showPasswords ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              required
              style={{
                width: '100%',
                padding: '12px',
                border: `1px solid ${EMZI_COLORS.border}`,
                borderRadius: EMZI_RADIUS.sm,
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Show Passwords Toggle */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <input
              type="checkbox"
              id="showPasswords"
              checked={showPasswords}
              onChange={(e) => setShowPasswords(e.target.checked)}
              style={{ marginRight: '8px' }}
            />
            <label htmlFor="showPasswords" style={{
              fontSize: '14px',
              color: EMZI_COLORS.textSecondary,
              cursor: 'pointer'
            }}>
              {t('auth.changePassword.showPasswords')}
            </label>
          </div>

          {/* Error Message */}
          {error && (
            <div style={{
              padding: '12px',
              background: '#fee2e2',
              border: '1px solid #ef4444',
              borderRadius: EMZI_RADIUS.sm,
              color: '#991b1b',
              fontSize: '14px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              background: loading ? EMZI_COLORS.textSecondary : EMZI_COLORS.primary,
              color: 'white',
              border: 'none',
              borderRadius: EMZI_RADIUS.sm,
              fontSize: '16px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              minHeight: '56px',
              transition: 'background 0.2s'
            }}
          >
            {loading ? 'Changing...' : t('auth.changePassword.submitButton')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
